<template>
    <div>
      <StackedHorizontalBarChart
        :title=chartTitle
        :filterContext="filterContext"
        :dataProviders="dataProviders"
        :contextUpdaters="contextUpdaters"
        :navigationConfig="navigationConfig"
        :chartFontColor="chartFontColor"
        @updateFilter="handleFilterUpdate"
      />
    </div>
  </template>
  
<script setup>
  import { computed } from 'vue';
  import { useStore } from 'vuex';
  import { useI18n } from 'vue-i18n';
  import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';

  
  const store = useStore();
  const { t } = useI18n();
  const filterContext = computed(() => store.state.filterItemsContext);
  const chartFontColor = computed(() => store.getters.chartFontColor);
  const conn = store.getters.getDBConnection;

  const chartName = 'drawingStackedBarChart';
  const chartTitle = t('drawingBreakdown');

  // Last drill-down filter query, so a status click keeps the current scope
  let currentDrillQuery = null;
  

const getDrawingSetData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      d.drawingSetTitle AS id,
      d.drawingSetId as parentId,
      d.drawingSetTitle AS title,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY d.drawingSetTitle, s.title, s.color) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN locations l ON i.locationId = l.id
    JOIN drawings d ON l.drawingId = d.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY d.drawingSetTitle, d.drawingSetId, s.title, s.color
    ORDER BY d.drawingSetTitle, sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};

const getDrawingData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      l.drawingId AS id,      
      l.drawingTitle AS title,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY l.drawingId, l.drawingTitle, s.title, s.color) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN locations l ON i.locationId = l.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY l.drawingId, l.drawingTitle, s.title, s.color
    ORDER BY l.drawingTitle, sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};
   
const getLocationData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      l.id AS id,      
      l.title AS title,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY l.id, l.title, s.title, s.color) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN locations l ON i.locationId = l.id
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY l.id, l.title, s.title, s.color
    ORDER BY l.title, sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};


const getStatusData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT
      s.title AS id,
      s.title AS title,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY s.title, s.color) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY s.title, s.color, s.sort
    ORDER BY s.sort, s.title
  `;

  return executeAndProcessQuery(query);
};


const getDrawingSetFilterContext = async (drawingSetTitle, drawingSetId) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN locations l ON i.locationId = l.id
    JOIN drawings d ON l.drawingId = d.id
    WHERE d.drawingSetId = '${drawingSetId}'
  `;
  currentDrillQuery = query;
  await updateFilteredItemsContext(query);
};

const getDrawingFilterContext = async (drawingId) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN locations l ON i.locationId = l.id
    WHERE l.drawingId = '${drawingId}'
  `;
  currentDrillQuery = query;
  await updateFilteredItemsContext(query);
};

const getLocationFilterContext = async (locationId) => {
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    WHERE i.locationId = '${locationId}'
  `;
  currentDrillQuery = query;
  await updateFilteredItemsContext(query);
};

const getStatusFilterContext = async (statusTitle) => {
  // Filter within the current drill-down scope when one is active
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN status_templates s ON i.StatusId = s.id
    WHERE s.title = '${statusTitle}'
    ${currentDrillQuery ? `AND i.SubItemId IN (${currentDrillQuery})` : ''}
  `;
  await updateFilteredItemsContext(query);
};

const resetFilterContext = async () => {
    currentDrillQuery = null;
    await filterContext.value.removeFilter(chartName);
}

  const dataProviders = {
    drawingsets: getDrawingSetData,
    drawings: getDrawingData,
    locations: getLocationData,
    status: getStatusData
  };
  
  const contextUpdaters = {
    drawingsets: getDrawingSetFilterContext,
    drawings: getDrawingFilterContext,
    locations: getLocationFilterContext,
    status: getStatusFilterContext,
    reset: resetFilterContext /*required*/
  };
  
  const navigationConfig = [
    { level: 'drawingsets', titleKey: t('drawingSets') },
    { level: 'drawings', titleKey: 'drawings' },
    { level: 'locations', titleKey: 'locations' },
    { level: 'status', titleKey: 'status' }
  ];


  const updateFilteredItemsContext = async (query) => {
    await filterContext.value.updateFilterWithQuery(chartName, query);
  };

  // Shared data processing utility function
const processChartData = (rows) => {
  const groupedData = {};
  
  rows.forEach(row => {
    const id = row['id'];
    const title = row['title'];
    const parentId = row['parentId'] ? row['parentId'] : null;
    const type = row['type'] ? row['type'] : null
    
    if (!groupedData[id]) {
      groupedData[id] = {
        name: title,
        id: id,
        parentId: parentId,
        type: type,
        children: []
      };
    }
    
    // Find if this color already exists in the children
    const existingColor = groupedData[id].children.find(
      child => child.color === row.color
    );
    
    if (existingColor) {
      existingColor.value += row.value;
      // Store the status names by color
      if (!existingColor.statusCounts) {
        existingColor.statusCounts = {};
      }
      existingColor.statusCounts[row.status_title] = row.value;
    } else {
      groupedData[id].children.push({
        color: row.color,
        value: row.value,
        statusName: row.status_title,
        sort: row.sort,
        statusCounts: { [row.status_title]: row.value }
      });
    }
  });
  
  return Object.values(groupedData).map(item => ({
    ...item,
    children: item.children.sort((a, b) => a.sort - b.sort)
  }));
};

// Generic query execution function
const executeAndProcessQuery = async (query, idField, titleField) => {
  const result = await conn.query(query);
  const rows = result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      value: Number(jsonRow.value)
    };
  });
  
  return processChartData(rows, idField, titleField);
};

  </script>